import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PlusCircle, Columns3, List } from "lucide-react";
import { VagaForm } from "@/components/vagas/VagaForm";
import { KanbanColumn } from "@/components/vagas/KanbanColumn";
import { VagasListView } from "@/components/vagas/list/VagasListView";
import { ReprovacaoModal } from "@/components/vagas/ReprovacaoModal";
import { AprovacaoModal } from "@/components/vagas/AprovacaoModal";
import { AprovacaoComProjetoDialog } from "@/components/vagas/AprovacaoComProjetoDialog";
import { approvalExitSummary, useApprovalExit } from "@/components/vagas/useApprovalExit";
import { logicalDateToTimestamp } from "@/utils/dateUtils";
import { ControleProjetoForm } from "@/components/controle-projetos/ControleProjetoForm";
import { Combobox } from "@/components/ui/combobox";
import { Vaga, VagaListagem } from "@/types/vaga";
import { useToast } from "@/hooks/use-toast";
import { supabase, SUPABASE_URL } from "@/integrations/supabase/client";
import type { TablesUpdate } from "@/integrations/supabase/types";
import { apiFetch } from "@/lib/api-fetch";
import { writeExpectingRows } from "@/lib/supabase-write";
import { trackEvent } from "@/lib/telemetry";
import { useNavigate } from "react-router";
import { handleSupabaseError } from "@/lib/supabase-error-handler";
import { addBreadcrumb } from "@/lib/sentry";
import { useAgencias } from "@/hooks/use-agencias";
import { useStaffUsers } from "@/hooks/use-staff-users";
import { useVagasViewMode } from "@/hooks/use-vagas-view-mode";
import { VAGA_STATUS_COLUMNS } from "@/utils/vaga-status";
import { cn } from "@/lib/utils";
import { useQueryClient, type InfiniteData } from "@tanstack/react-query";
import { toUserError } from "@/lib/api-error";
import { reportUserError, userErrorToast } from "@/lib/report-error";

// Fonte única das colunas do board (status da vaga) — ver src/utils/vaga-status.ts.
const statusColumns = VAGA_STATUS_COLUMNS;

export default function Vagas() {
  const queryClient = useQueryClient();
  // Agências e usuários internos vêm de hooks com cache compartilhado
  // (TanStack Query) em vez de fetch cru — deduplica as chamadas em /vagas.
  const { data: usuarios = [] } = useStaffUsers();
  const { data: agencias = [] } = useAgencias();
  const { mode, setMode } = useVagasViewMode();
  const [agenciaFilter, setAgenciaFilter] = useState<string>("");
  const [novaVaga, setNovaVaga] = useState<Partial<Vaga>>({
    status: "entrada",
    agencia: "",
    agencia_id: "",
    cliente: "",
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [vagaParaExcluir, setVagaParaExcluir] = useState<string | null>(null);
  const [isReprovacaoModalOpen, setIsReprovacaoModalOpen] = useState(false);
  const [vagaToReprovar, setVagaToReprovar] = useState<string | null>(null);
  const [isReprovando, setIsReprovando] = useState(false);
  const [isAprovacaoModalOpen, setIsAprovacaoModalOpen] = useState(false);
  const [vagaToAprovar, setVagaToAprovar] = useState<string | null>(null);
  const [isAprovando, setIsAprovando] = useState(false);
  const [aprovacaoSugestao, setAprovacaoSugestao] = useState<{
    freelancerId: string | null;
    freelancerNome: string | null;
    multiplos: boolean;
    verbaMinima: number | null;
    verbaMaxima: number | null;
    moeda?: string | null;
  }>({
    freelancerId: null,
    freelancerNome: null,
    multiplos: false,
    verbaMinima: null,
    verbaMaxima: null,
  });
  const [isProjetoDialogOpen, setIsProjetoDialogOpen] = useState(false);
  const [projetoParaEditar, setProjetoParaEditar] = useState<Record<string, unknown> | null>(null);
  // Fluxo de dedup (vaga já tem projeto vinculado): não cria outro projeto, então
  // abre o AprovacaoComProjetoDialog (data de aprovação + encerrar demais).
  const [vagaComProjetoParaAprovar, setVagaComProjetoParaAprovar] = useState<{
    vagaId: string;
    freelancerAprovadoId: string | null;
  } | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  const approvalExit = useApprovalExit();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef<number | null>(null);

  // O kanban agora é paginado POR COLUNA (cada <KanbanColumn> tem seu próprio
  // useVagaColumn). A página não mantém mais um array único com todas as vagas.
  // Helpers abaixo refletem as mutações nas queries das colunas.

  type VagaColumnData = InfiniteData<{ rows: VagaListagem[] }>;

  // Acha uma vaga carregada (visível) procurando nas caches de todas as colunas.
  const getCachedVaga = (vagaId: string): VagaListagem | undefined => {
    for (const [, data] of queryClient.getQueriesData<VagaColumnData>({ queryKey: ["vagas-column"] })) {
      for (const page of data?.pages ?? []) {
        const found = page.rows.find((v) => v.id === vagaId);
        if (found) return found;
      }
    }
    return undefined;
  };

  // Remove a vaga das caches de todas as colunas (feedback otimista instantâneo
  // ao mover/excluir; o refreshBoard reconcilia ordem e contagens).
  const removeFromBoard = (vagaId: string) => {
    for (const [key, data] of queryClient.getQueriesData<VagaColumnData>({ queryKey: ["vagas-column"] })) {
      if (!data) continue;
      queryClient.setQueryData<VagaColumnData>(key, {
        ...data,
        pages: data.pages.map((p) => ({
          ...p,
          rows: p.rows.filter((v) => v.id !== vagaId),
        })),
      });
    }
  };

  // Revalida todas as colunas (ordem) + as contagens dos badges após mutação.
  const refreshBoard = () => {
    queryClient.invalidateQueries({ queryKey: ["vagas-column"] });
    queryClient.invalidateQueries({ queryKey: ["vagas-counts"] });
  };

  const isApproved = (vagaId: string) => getCachedVaga(vagaId)?.status === "aprovado";

  // Leaving Aprovado goes through the API, which also deletes the approval drafts and clears the date.
  const moveOutOfApproved = async (vagaId: string, status: Vaga["status"], successMessage: string) => {
    try {
      const result = await approvalExit.apply(vagaId, { status_destino: status });
      removeFromBoard(vagaId);
      refreshBoard();
      toast({
        title: "Sucesso",
        description: `${successMessage} ${approvalExitSummary(result)}`.trim(),
      });
    } catch (error) {
      toast(userErrorToast(error, { action: "tirar a vaga de Aprovado", source: "vagas" }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Adicionar breadcrumb antes da operação
      addBreadcrumb('Creating vaga', {
        agencia: novaVaga.agencia,
        cliente: novaVaga.cliente,
        areas: novaVaga.areas,
      });

      // Create the vaga first
      const { data: vagaInserida, error: vagaError } = await supabase
        .from("vagas")
        .insert([{
          agencia: novaVaga.agencia || '',
          agencia_id: novaVaga.agencia_id || null,
          cliente: novaVaga.cliente || '',
          status: novaVaga.status,
          descricao: novaVaga.descricao,
          descricao_curta: novaVaga.descricao_curta || null,
          areas: novaVaga.areas,
          verba: novaVaga.verba, // Keep for backward compatibility
          verba_minima: novaVaga.verba_minima || null,
          verba_maxima: novaVaga.verba_maxima || null,
          ...(novaVaga.moeda ? { moeda: novaVaga.moeda } : {}),
          formato: novaVaga.formato,
          tipo_contrato: novaVaga.tipo_contrato,
          modalidade: novaVaga.modalidade || null,
          requisitos: novaVaga.requisitos,
          duracao: novaVaga.duracao, // Keep for backward compatibility
          duracao_dias: novaVaga.duracao_dias || null,
          localizacao_cidade: novaVaga.localizacao_cidade,
          localizacao_bairro: novaVaga.localizacao_bairro,
          localizacao_estado: novaVaga.localizacao_estado,
          regime_contratacao: novaVaga.regime_contratacao,
          deadline_preenchimento: novaVaga.deadline_preenchimento || null,
          complexidade: novaVaga.complexidade || null,
          urgencia: novaVaga.urgencia || null,
          // last_status_changed_at é carimbado server-side (DEFAULT/trigger);
          // não enviar valor do cliente para evitar erro de clock skew na
          // constraint vagas_last_status_changed_at_not_future.
        }])
        .select()
        .single();

      if (vagaError) {
        // Capturar erro com contexto completo
        handleSupabaseError(vagaError, 'insert_vaga', {
          payload: novaVaga,
          table: 'vagas',
        });
        throw vagaError;
      }

      // If area_id is provided, create an entry in vaga_areas
      /*if (novaVaga.area_id) {
        console.log("Associando área:", { vaga_id: vagaInserida.id, area_id: novaVaga.area_id });
        
        const { error: areaError } = await supabase
          .from("vaga_areas")
          .insert([{
            vaga_id: vagaInserida.id,
            area_id: novaVaga.area_id
          }]);

        if (areaError) {
          console.error("Erro ao associar área:", areaError);
          throw areaError;
        }
      }*/

      // Fetch the updated vaga with all information
      const { data: vagaAtualizada, error: selectError } = await supabase
        .from("vagas")
        .select("*")
        .eq("id", vagaInserida.id)
        .single();

      if (selectError) throw selectError;

      // O ranking da vaga já nasce calculado (a lista aberta pela vaga depende dele). Não segura
      // a tela; se falhar, o cron de /recommendations/maintenance/rank-pending ranqueia depois.
      void apiFetch(`${SUPABASE_URL}recommendations/${vagaInserida.id}?limit=100&dual=true&motivo=criacao`).catch(
        () => undefined,
      );

      refreshBoard();
      setNovaVaga({ status: "entrada", agencia: "", agencia_id: "", cliente: "" });
      setIsDialogOpen(false);
      toast({
        title: "Sucesso",
        description: "Vaga criada com sucesso!",
      });
    } catch (error) {
      console.error("Erro ao criar vaga:", error);
      toast({
        title: "Erro",
        description: "Não foi possível criar a vaga. " + reportUserError(error, { source: "vagas" }).description,
        variant: "destructive",
      });
    }
  };

  const handleEdit = (vagaId: string) => {
    navigate(`/vagas/${vagaId}`);
  };

  const handleDelete = async (vagaId: string) => {
    try {
      // Soft delete: marca deleted_at. A `vagas` é uma view que filtra
      // deleted_at IS NULL, então a vaga some de todas as telas/métricas/Metabase
      // sem hard delete (que quebrava no CASCADE do kanban). Reversível no banco.
      const { error } = await supabase
        .from("vagas")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", vagaId);

      if (error) throw error;

      removeFromBoard(vagaId);
      refreshBoard();
      
      toast({
        title: "Sucesso",
        description: "Vaga excluída com sucesso!",
      });
    } catch (error) {
      console.error("Erro ao excluir vaga:", error);
      toast({
        title: "Erro",
        description: "Não foi possível excluir a vaga. " + reportUserError(error, { source: "vagas" }).description,
        variant: "destructive",
      });
    }
  };

  const handleDragStart = (e: React.DragEvent, vagaId: string) => {
    e.dataTransfer.setData("vagaId", vagaId);
    setIsDragging(true);
  };

  const handleDrop = async (e: React.DragEvent, status: Vaga["status"]) => {
    e.preventDefault();
    const vagaId = e.dataTransfer.getData("vagaId");

    // Se o status for "reprovado", abrir o modal
    if (status === 'reprovado') {
      if (isApproved(vagaId)) {
        void approvalExit.request(vagaId, () => handleRequestReprovacao(vagaId));
      } else {
        handleRequestReprovacao(vagaId);
      }
      return;
    }

    // Se o status for "aprovado", abrir o modal de aprovação
    if (status === 'aprovado') {
      await handleRequestAprovacao(vagaId);
      return;
    }

    if (isApproved(vagaId)) {
      void approvalExit.request(vagaId, () =>
        void moveOutOfApproved(vagaId, status, "Status da vaga atualizado com sucesso!"),
      );
      return;
    }
    
    try {
      const { data: vagaAtualizada, error } = await supabase
        .from("vagas")
        .update({
          status,
        })
        .eq("id", vagaId)
        .select("last_status_changed_at")
        .single();

      if (error) throw error;

      removeFromBoard(vagaId);
      refreshBoard();

      toast({
        title: "Sucesso",
        description: "Status da vaga atualizado com sucesso!",
      });
    } catch (error) {
      console.error("Erro ao atualizar status:", error);
      toast({
        title: "Erro",
        description: "Não foi possível atualizar o status da vaga. " + reportUserError(error, { source: "vagas" }).description,
        variant: "destructive",
      });
    }
  };

  const handleVagaClick = (vagaId: string) => {
    trackEvent("vagas.vaga_abrir", { entidade: { tipo: "vaga", id: vagaId } });
    navigate(`/vagas/${vagaId}`);
  };

  const handleResponsavelChange = async (vagaId: string, responsavel: string) => {
    try {
      // Tratar o valor especial para remoção
      const responsavelValue = responsavel === "__REMOVE__" ? null : responsavel;
      
      const { error } = await supabase
        .from("vagas")
        .update({ responsavel: responsavelValue })
        .eq("id", vagaId);

      if (error) throw error;

      refreshBoard();

      toast({
        title: "Sucesso",
        description: "Responsável atribuído com sucesso ao freelancer!",
      });
    } catch (error) {
      console.error("Erro ao atribuir responsável:", error);
      toast({
        title: "Erro",
        description: "Não foi possível atribuir o responsável ao freelancer. " + reportUserError(error, { source: "vagas" }).description,
        variant: "destructive",
      });
    }
  };

  const handleMoveToStatus = async (vagaId: string, status: Vaga["status"]) => {
    if (status !== "aprovado" && isApproved(vagaId)) {
      void approvalExit.request(vagaId, () => void moveOutOfApproved(vagaId, status, "Vaga movida com sucesso!"));
      return;
    }
    try {
      const { data: vagaAtualizada, error } = await supabase
        .from("vagas")
        .update({
          status,
        })
        .eq("id", vagaId)
        .select("last_status_changed_at")
        .single();

      if (error) throw error;

      removeFromBoard(vagaId);
      refreshBoard();

      toast({
        title: "Sucesso",
        description: "Vaga movida com sucesso!",
      });
    } catch (error) {
      console.error("Erro ao mover vaga:", error);
      toast({
        title: "Erro",
        description: "Não foi possível mover a vaga. " + reportUserError(error, { source: "vagas" }).description,
        variant: "destructive",
      });
    }
  };

  const handleRequestReprovacao = (vagaId: string) => {
    setVagaToReprovar(vagaId);
    setIsReprovacaoModalOpen(true);
  };

  const handleConfirmReprovacao = async (data: {
    tipo_reprovacao: string;
    reprovado_por: 'corisco' | 'cliente';
    motivo_reprovacao: string;
  }) => {
    if (!vagaToReprovar) return;

    setIsReprovando(true);
    try {
      if (isApproved(vagaToReprovar)) {
        await approvalExit.apply(vagaToReprovar, { status_destino: "reprovado", ...data });
      } else {
        const { error } = await supabase
          .from("vagas")
          .update({
            status: 'reprovado',
            tipo_reprovacao: data.tipo_reprovacao,
            reprovado_por: data.reprovado_por,
            motivo_reprovacao: data.motivo_reprovacao,
          })
          .eq("id", vagaToReprovar)
          .select("last_status_changed_at")
          .single();

        if (error) throw error;
      }

      removeFromBoard(vagaToReprovar);
      refreshBoard();

      toast({
        title: "Sucesso",
        description: "Vaga reprovada com sucesso!",
      });

      setIsReprovacaoModalOpen(false);
      setVagaToReprovar(null);
    } catch (error) {
      console.error("Erro ao reprovar vaga:", error);
      toast({
        title: "Erro",
        description: "Não foi possível reprovar a vaga. " + reportUserError(error, { source: "vagas" }).description,
        variant: "destructive",
      });
    } finally {
      setIsReprovando(false);
    }
  };

  // Dropa em lote os candidatos ainda ativos no kanban da vaga (RPC
  // encerrar_candidatos_restantes, sql/160). O freelancer aprovado é excluído
  // do lote na própria RPC: quando o vencedor foi escolhido manualmente no
  // modal ele pode ainda estar numa etapa ativa do kanban, e sem a exclusão o
  // lote encerraria a própria pessoa que ganhou a vaga. Nunca lança: a vaga já
  // foi aprovada quando isso é chamado, então uma falha aqui não pode derrubar
  // a aprovação — só avisa o usuário que o encerramento em lote não rodou.
  const encerrarCandidatosRestantes = async (
    vagaId: string,
    freelancerAprovadoId: string | null,
  ): Promise<number> => {
    try {
      const { data, error } = await supabase.rpc("encerrar_candidatos_restantes", {
        p_vaga_id: vagaId,
        p_freelancer_aprovado_id: freelancerAprovadoId,
      });
      if (error) throw error;
      return typeof data === "number" ? data : 0;
    } catch (error) {
      const err = error as Error;
      console.error("Erro ao encerrar candidatos restantes:", err);
      handleSupabaseError(err, "encerrar candidatos restantes da vaga");
      toast({
        title: "Candidatos não encerrados",
        description:
          "A vaga foi aprovada, mas não foi possível encerrar os demais candidatos automaticamente. " +
          toUserError(err).description,
        variant: "destructive",
      });
      return 0;
    }
  };

  const handleRequestAprovacao = async (vagaId: string) => {
    const vaga = getCachedVaga(vagaId);
    if (!vaga) return;

    // Dedup: se já existe um controle_projetos para esta vaga, só atualiza status
    try {
      const { data: existentes } = await supabase
        .from("controle_projetos")
        .select("id, freelancer_id")
        .eq("vaga_id", vagaId)
        .is("deleted_at", null)
        .limit(1);

      if (existentes && existentes.length > 0) {
        // O freelancer do projeto existente é o vencedor: nunca entra no lote
        // de "encerrar demais".
        setVagaComProjetoParaAprovar({
          vagaId,
          freelancerAprovadoId: existentes[0].freelancer_id ?? null,
        });
        return;
      }
    } catch (error) {
      console.error("Erro ao checar projetos existentes:", error);
    }

    // Buscar freelancer na etapa "Aprovado" do kanban interno da vaga
    let freelancerId: string | null = null;
    let freelancerNome: string | null = null;
    let multiplos = false;
    try {
      const { data: etapas } = await supabase
        .from("vagas_kanban_etapas")
        .select("id, nome")
        .eq("vaga_id", vagaId)
        .ilike("nome", "Aprovado");

      const etapaAprovado = (etapas || [])[0];
      if (etapaAprovado) {
        const { data: posicoes } = await supabase
          .from("vagas_kanban_posicoes")
          .select("freelancer_id, data_adicao, freelancers(nome)")
          .eq("vaga_id", vagaId)
          .eq("etapa_atual_id", etapaAprovado.id)
          .or("is_dropped.is.null,is_dropped.eq.false")
          .order("data_adicao", { ascending: false });

        if (posicoes && posicoes.length > 0) {
          multiplos = posicoes.length > 1;
          freelancerId = posicoes[0].freelancer_id;
          // freelancers vem como objeto ou array dependendo da relação
          type FreelancerRel = { nome?: string | null } | { nome?: string | null }[] | null;
          const rel = (posicoes[0] as { freelancers?: FreelancerRel }).freelancers;
          const f = Array.isArray(rel) ? rel[0] : rel;
          freelancerNome = f?.nome ?? null;
        }
      }
    } catch (error) {
      console.error("Erro ao buscar freelancer aprovado:", error);
    }

    setVagaToAprovar(vagaId);
    setAprovacaoSugestao({
      freelancerId,
      freelancerNome,
      multiplos,
      verbaMinima: vaga.verba_minima ?? null,
      verbaMaxima: vaga.verba_maxima ?? null,
      moeda: vaga.moeda,
    });
    setIsAprovacaoModalOpen(true);
  };

  const handleConfirmAprovacao = async (data: {
    freelancer_id: string;
    valor_projeto: number;
    encerrar_demais: boolean;
    data_aprovacao: string;
  }) => {
    if (!vagaToAprovar) return;
    const vaga = getCachedVaga(vagaToAprovar);
    if (!vaga) return;

    setIsAprovando(true);
    try {
      // 1. Atualiza status da vaga
      const { data: vagaAprovada, error: vagaError } = await supabase
        .from("vagas")
        .update({
          status: 'aprovado',
          // last_status_changed_at é carimbado server-side pelo trigger ao
          // mudar o status; não enviar valor do cliente (clock skew).
          data_aprovacao: logicalDateToTimestamp(data.data_aprovacao),
        })
        .eq("id", vagaToAprovar)
        .select("last_status_changed_at")
        .single();
      if (vagaError) throw vagaError;

      // 2. Cria controle_projetos em rascunho (auto-fill agencia + area da vaga)
      const areaAtuacao = vaga.areas || "";
      const insertPayload = {
        vaga_id: vagaToAprovar,
        agencia_id: vaga.agencia_id || null,
        area_atuacao: areaAtuacao,
        freelancer_id: data.freelancer_id,
        valor_projeto: data.valor_projeto,
        modalidade: vaga.modalidade === 'fixa' ? 'fixa' : 'frila',
        status: 'rascunho' as const,
      };
      await writeExpectingRows(
        supabase.from("controle_projetos").insert(insertPayload),
        "criar rascunho do projeto",
      );

      addBreadcrumb('vaga.aprovacao.criou_rascunho', {
        vaga_id: vagaToAprovar,
        freelancer_id: data.freelancer_id,
      });

      // 3. Encerra os demais candidatos do kanban, se pedido no checkbox. Vem
      // DEPOIS do UPDATE de status: o trigger de preterido (sql/148) só conta
      // quem ainda está ativo no momento em que a vaga vira aprovada.
      let totalCandidatosEncerrados = 0;
      if (data.encerrar_demais) {
        totalCandidatosEncerrados = await encerrarCandidatosRestantes(
          vagaToAprovar,
          data.freelancer_id,
        );
      }

      removeFromBoard(vagaToAprovar);
      refreshBoard();

      toast({
        title: "Vaga aprovada",
        description:
          totalCandidatosEncerrados > 0
            ? `Projeto criado em rascunho. ${totalCandidatosEncerrados} candidato${totalCandidatosEncerrados > 1 ? "s" : ""} encerrado${totalCandidatosEncerrados > 1 ? "s" : ""} do processo. Complete os dados em Projetos.`
            : "Projeto criado em rascunho. Complete os dados em Projetos.",
      });

      setIsAprovacaoModalOpen(false);
      setVagaToAprovar(null);
      setAprovacaoSugestao({
        freelancerId: null,
        freelancerNome: null,
        multiplos: false,
        verbaMinima: null,
        verbaMaxima: null,
      });
    } catch (error) {
      const err = error as Error;
      console.error("Erro ao aprovar vaga:", err);
      handleSupabaseError(err, "aprovar vaga");
      toast({
        title: "Erro",
        description: "Não foi possível aprovar a vaga. " + toUserError(err).description,
        variant: "destructive",
      });
    } finally {
      setIsAprovando(false);
    }
  };

  // Aprovação no caminho de dedup (vaga que já tinha projeto vinculado): grava status
  // e data de aprovação juntos, como o AprovacaoModal, e encerra os demais se pedido.
  const handleConfirmAprovacaoComProjeto = async (data: {
    encerrar_demais: boolean;
    data_aprovacao: string;
  }) => {
    const alvo = vagaComProjetoParaAprovar;
    if (!alvo) return;

    setIsAprovando(true);
    try {
      const { error: vagaError } = await supabase
        .from("vagas")
        .update({
          status: 'aprovado',
          data_aprovacao: logicalDateToTimestamp(data.data_aprovacao),
        })
        .eq("id", alvo.vagaId)
        .select("last_status_changed_at")
        .single();
      if (vagaError) throw vagaError;

      let totalCandidatosEncerrados = 0;
      if (data.encerrar_demais) {
        totalCandidatosEncerrados = await encerrarCandidatosRestantes(
          alvo.vagaId,
          alvo.freelancerAprovadoId,
        );
      }

      removeFromBoard(alvo.vagaId);
      refreshBoard();

      toast({
        title: "Vaga aprovada",
        description:
          totalCandidatosEncerrados > 0
            ? `A vaga já tinha projeto vinculado, nenhum projeto novo foi criado. ${totalCandidatosEncerrados} candidato${totalCandidatosEncerrados > 1 ? "s" : ""} encerrado${totalCandidatosEncerrados > 1 ? "s" : ""} do processo.`
            : "A vaga já tinha projeto vinculado, nenhum projeto novo foi criado.",
      });

      setVagaComProjetoParaAprovar(null);
    } catch (error) {
      const err = error as Error;
      console.error("Erro ao aprovar vaga:", err);
      handleSupabaseError(err, "aprovar vaga");
      toast({
        title: "Erro",
        description: "Não foi possível aprovar a vaga. " + toUserError(err).description,
        variant: "destructive",
      });
    } finally {
      setIsAprovando(false);
    }
  };

  // Edita o projeto vinculado (controle_projetos) direto do card da vaga.
  // Busca a linha completa porque a view v_vagas_listagem só traz um resumo —
  // abrir o form só com o resumo apagaria agência/área/núcleo/data de conclusão.
  const handleEditProjeto = async (vagaId: string) => {
    const vaga = getCachedVaga(vagaId);
    if (!vaga?.projeto_id) {
      toast({
        title: "Sem projeto vinculado",
        description: "Esta vaga ainda não tem um projeto para editar.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data, error } = await supabase
        .from("controle_projetos")
        .select("id, agencia_id, freelancer_id, area_atuacao, nucleo, valor_projeto, data_inicio, data_conclusao, vaga_id")
        .eq("id", vaga.projeto_id)
        .single();
      if (error) throw error;

      setProjetoParaEditar(data as Record<string, unknown>);
      setIsProjetoDialogOpen(true);
    } catch (error) {
      const err = error as Error;
      console.error("Erro ao carregar projeto vinculado:", err);
      toast({
        title: "Erro",
        description: "Não foi possível carregar o projeto vinculado. " + reportUserError(err, { source: "vagas" }).description,
        variant: "destructive",
      });
    }
  };

  const handleSaveProjeto = async (projetoData: TablesUpdate<"controle_projetos">) => {
    if (!projetoParaEditar?.id) return;
    try {
      await writeExpectingRows(
        supabase
          .from("controle_projetos")
          .update(projetoData)
          .eq("id", projetoParaEditar.id as string),
        "atualizar projeto",
      );

      toast({
        title: "Projeto atualizado",
        description: "Os dados do projeto foram salvos.",
      });

      setIsProjetoDialogOpen(false);
      setProjetoParaEditar(null);
      // Recarrega as vagas para refletir o novo valor/freelancer no card.
      refreshBoard();
    } catch (error) {
      const err = error as Error;
      console.error("Erro ao salvar projeto:", err);
      handleSupabaseError(err, "editar projeto vinculado");
      toast({
        title: "Erro",
        description: "Não foi possível salvar o projeto. " + toUserError(err).description,
        variant: "destructive",
      });
    }
  };

  const handleAutoScroll = (e: React.DragEvent) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const scrollZone = 120; // Área em pixels nas bordas onde o scroll deve acontecer
    const maxScrollSpeed = 25; // Velocidade máxima do scroll
    const minScrollSpeed = 8; // Velocidade mínima do scroll

    const mouseX = e.clientX;
    const leftEdge = containerRect.left;
    const rightEdge = containerRect.right;

    // Limpa qualquer scroll automático anterior
    if (autoScrollRef.current) {
      clearInterval(autoScrollRef.current);
      autoScrollRef.current = null;
    }

    // Verifica se está na zona de scroll à esquerda
    if (mouseX - leftEdge < scrollZone && container.scrollLeft > 0) {
      const distanceFromEdge = mouseX - leftEdge;
      const scrollSpeed = Math.max(
        minScrollSpeed,
        maxScrollSpeed * (1 - distanceFromEdge / scrollZone)
      );
      
      autoScrollRef.current = window.setInterval(() => {
        if (container.scrollLeft > 0) {
          container.scrollLeft -= scrollSpeed;
        } else {
          if (autoScrollRef.current) {
            clearInterval(autoScrollRef.current);
            autoScrollRef.current = null;
          }
        }
      }, 16); // ~60fps
    }
    // Verifica se está na zona de scroll à direita
    else if (rightEdge - mouseX < scrollZone && container.scrollLeft < container.scrollWidth - container.clientWidth) {
      const distanceFromEdge = rightEdge - mouseX;
      const scrollSpeed = Math.max(
        minScrollSpeed,
        maxScrollSpeed * (1 - distanceFromEdge / scrollZone)
      );
      
      autoScrollRef.current = window.setInterval(() => {
        if (container.scrollLeft < container.scrollWidth - container.clientWidth) {
          container.scrollLeft += scrollSpeed;
        } else {
          if (autoScrollRef.current) {
            clearInterval(autoScrollRef.current);
            autoScrollRef.current = null;
          }
        }
      }, 16); // ~60fps
    }
  };

  const stopAutoScroll = () => {
    if (autoScrollRef.current) {
      clearInterval(autoScrollRef.current);
      autoScrollRef.current = null;
    }
    setIsDragging(false);
  };

  // Limpa o auto scroll quando o componente desmonta
  useEffect(() => {
    return () => {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
      }
    };
  }, []);
  
  return (
    <div className="h-full flex flex-col fade-in">
      <div className="flex flex-col gap-3 pb-3 border-b border-slate-200 sm:flex-row sm:items-center sm:justify-between sm:pb-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Vagas</h1>
          <p className="text-sm text-slate-600 mt-0.5">
            Gerencie as vagas e processos seletivos
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Alterna entre o board kanban (principal) e a visão em lista */}
          <div className="flex items-center rounded-md border border-slate-200 bg-white p-0.5">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Visão kanban"
              aria-pressed={mode === "kanban"}
              onClick={() => setMode("kanban")}
              className={cn(
                "h-8 w-8",
                mode === "kanban" && "bg-slate-100 text-slate-900",
              )}
            >
              <Columns3 className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Visão em lista"
              aria-pressed={mode === "list"}
              onClick={() => setMode("list")}
              className={cn(
                "h-8 w-8",
                mode === "list" && "bg-slate-100 text-slate-900",
              )}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>

          {mode === "kanban" && (
            <Combobox
              className="w-full sm:w-56 bg-white"
              allowCustomValue={false}
              options={[
                { value: "", label: "Todas as agências" },
                ...agencias.map((a) => ({ value: a.id, label: a.nome })),
              ]}
              value={agenciaFilter}
              onValueChange={setAgenciaFilter}
              placeholder="Filtrar por agência"
              searchPlaceholder="Buscar agência..."
              emptyText="Nenhuma agência encontrada."
            />
          )}

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2 bg-linear-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all duration-200">
                <PlusCircle className="h-4 w-4" />
                Nova Vaga
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto border-0 shadow-2xl">
              <DialogHeader>
                <DialogTitle className="text-xl font-semibold text-slate-900">Criar Nova Vaga</DialogTitle>
              </DialogHeader>
              <VagaForm
                novaVaga={novaVaga}
                setNovaVaga={setNovaVaga}
                onSubmit={handleSubmit}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>
      
      {mode === "list" ? (
        <div className="flex-1 overflow-hidden">
          <VagasListView />
        </div>
      ) : (
      <div className="flex-1 overflow-hidden relative bg-linear-to-br from-slate-50 to-blue-50/30">
        {/* Indicadores de scroll automático */}
        {isDragging && (
          <>
            <div className="absolute left-0 top-0 bottom-0 w-24 z-20 pointer-events-none bg-linear-to-r from-blue-400/20 to-transparent flex items-center justify-center transition-all duration-300">
              <div className="text-blue-600 text-xs font-medium bg-white/95 px-2 py-1.5 rounded-lg shadow-lg border border-blue-200 backdrop-blur-xs">
                ← Scroll
              </div>
            </div>
            <div className="absolute right-0 top-0 bottom-0 w-24 z-20 pointer-events-none bg-linear-to-l from-blue-400/20 to-transparent flex items-center justify-center transition-all duration-300">
              <div className="text-blue-600 text-xs font-medium bg-white/95 px-2 py-1.5 rounded-lg shadow-lg border border-blue-200 backdrop-blur-xs">
                Scroll →
              </div>
            </div>
          </>
        )}
        
        <div 
          ref={scrollContainerRef}
          className="h-full flex gap-4 overflow-x-auto p-6 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent"
          onDragOver={handleAutoScroll}
          onDragLeave={stopAutoScroll}
          onDrop={stopAutoScroll}
          onDragEnd={stopAutoScroll}
        >
          {statusColumns.map((column) => (
            <KanbanColumn
              key={column.id}
              title={column.title}
              status={column.id}
              agenciaId={agenciaFilter}
              onDragStart={handleDragStart}
              onDrop={(e) => handleDrop(e, column.id)}
              onVagaClick={handleVagaClick}
              onResponsavelChange={handleResponsavelChange}
              onEdit={handleEdit}
              onDelete={setVagaParaExcluir}
              onMoveToStatus={handleMoveToStatus}
              onRequestReprovacao={handleRequestReprovacao}
              onRequestAprovacao={handleRequestAprovacao}
              onEditProjeto={handleEditProjeto}
              usuarios={usuarios}
            />
          ))}
        </div>
      </div>
      )}

      {/* Modal de Reprovação */}
      <ReprovacaoModal
        open={isReprovacaoModalOpen}
        onOpenChange={setIsReprovacaoModalOpen}
        onConfirm={handleConfirmReprovacao}
        isLoading={isReprovando}
      />

      {/* Modal de Aprovação */}
      <AprovacaoModal
        open={isAprovacaoModalOpen}
        onOpenChange={(open) => {
          setIsAprovacaoModalOpen(open);
          if (!open) {
            setVagaToAprovar(null);
            setAprovacaoSugestao({
              freelancerId: null,
              freelancerNome: null,
              multiplos: false,
              verbaMinima: null,
              verbaMaxima: null,
            });
          }
        }}
        onConfirm={handleConfirmAprovacao}
        isLoading={isAprovando}
        vagaId={vagaToAprovar}
        freelancerSugeridoId={aprovacaoSugestao.freelancerId}
        freelancerSugeridoNome={aprovacaoSugestao.freelancerNome}
        multiplosFreelancersAprovados={aprovacaoSugestao.multiplos}
        verbaMinima={aprovacaoSugestao.verbaMinima}
        verbaMaxima={aprovacaoSugestao.verbaMaxima}
        moeda={aprovacaoSugestao.moeda}
      />

      <AlertDialog open={!!vagaParaExcluir} onOpenChange={(open) => !open && setVagaParaExcluir(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir esta vaga?</AlertDialogTitle>
            <AlertDialogDescription>
              Ela sai do quadro, das listas e dos relatórios, junto com o kanban de candidatos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Manter vaga</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (vagaParaExcluir) void handleDelete(vagaParaExcluir);
                setVagaParaExcluir(null);
              }}
            >
              Excluir vaga
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Aprovação de vaga que já tinha projeto vinculado (não cria outro projeto). */}
      <AprovacaoComProjetoDialog
        open={!!vagaComProjetoParaAprovar}
        onOpenChange={(open) => {
          if (!open) setVagaComProjetoParaAprovar(null);
        }}
        onConfirm={handleConfirmAprovacaoComProjeto}
        isLoading={isAprovando}
        vagaId={vagaComProjetoParaAprovar?.vagaId ?? null}
        freelancerAprovadoId={vagaComProjetoParaAprovar?.freelancerAprovadoId ?? null}
      />

      {approvalExit.dialogs}

      {/* Edição do projeto vinculado (valor, freelancer, datas...) a partir do card */}
      <Dialog
        open={isProjetoDialogOpen}
        onOpenChange={(open) => {
          setIsProjetoDialogOpen(open);
          if (!open) setProjetoParaEditar(null);
        }}
      >
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Projeto</DialogTitle>
          </DialogHeader>
          {projetoParaEditar && (
            <ControleProjetoForm
              projeto={projetoParaEditar}
              mode="edit"
              onSave={handleSaveProjeto}
              onCancel={() => {
                setIsProjetoDialogOpen(false);
                setProjetoParaEditar(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
